/**
 * Generates an error message when a value is not a complex object.
 * 
 * @param guardName - The name of the variable being guarded.
 * @returns A formatted error message.
 */
export const notComplexObjectMessage = (guardName: string): string => (
  `'${guardName}' must be a complex object`
)

/**
 * Throws an error if the provided value is not a complex object.
 * 
 * A complex object is defined as a non-null object that is not a primitive and whose constructor is `Object`.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guard - The value to check.
 * @throws {Error} Throws an error if `isComplexObject(guard)` is false.
 */
export const throwNotComplexObject = (guardName: string, guard: any): void => {
  if (isComplexObject(guard) === false) {
    throw new Error(notComplexObjectMessage(guardName));
  }
}

/**
 * Determines if a value is a complex object.
 * 
 * @param value - The value to test.
 * @returns `true` if the value is a defined, non-null object with a constructor matching `Object`.
 */
export const isComplexObject = (value: any): boolean => {
  const isDefined = value !== undefined && value !== null;
  return isDefined
    && value.constructor
    && value.constructor === Object;
}